import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLeadFormSubmit } from '@/hooks/useLeadFormSubmit';
import { inputClass, labelClass } from '@/components/forms/leadFormStyles';

const CONTACT_REASONS = [
  {
    title: 'Book a working session',
    description:
      "A working session on your estate beats a canned demo — bring your claims or underwriting workflow and we'll map it live.",
  },
  {
    title: 'Start the Discovery Trial',
    description: 'See every agent you own in 30 days — read-only and reversible.',
  },
  {
    title: 'Partnerships',
    description:
      'Core-system, insurtech, and security-tooling integrations — our discovery ingests your findings as first-class sources.',
  },
  {
    title: 'Careers & press',
    description:
      'Join the team shaping governed agentic AI for insurance, or reach our media desk.',
  },
];

export function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    subject: 'demo',
    message: '',
    website: '', // honeypot field
  });

  const { isSubmitting, status: submitStatus, errorMessage, submit, reset } = useLeadFormSubmit();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await submit({
      formType: 'contact',
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      company: formData.company,
      subject: formData.subject,
      message: formData.message,
      website: formData.website,
    });
    if (success) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        subject: 'demo',
        message: '',
        website: '',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - CriticalBridge.AI</title>
        <meta
          name="description"
          content="Get in touch with the CriticalBridge.AI team. We're here to help with working sessions, the Discovery Trial, partnerships, and careers."
        />
        <meta
          name="keywords"
          content="contact criticalbridge, agentic AI insurance support, discovery trial sales"
        />
        <meta property="og:title" content="Contact Us - CriticalBridge.AI" />
        <meta property="og:description" content="Get in touch with our team" />
        <link rel="canonical" href="https://www.criticalbridge.ai/contact" />
      </Helmet>

      {/* Hero */}
      <section className="bg-secondary">
        <div className="container mx-auto px-4 py-16 sm:py-20">
          <div className="font-mono text-xs text-secondary-foreground/60">
            <Link to="/" className="hover:text-secondary-foreground">
              Home
            </Link>{' '}
            / Company / Contact
          </div>
          <h1 className="mt-4 max-w-[22ch] text-4xl font-extrabold leading-tight text-secondary-foreground sm:text-5xl">
            Let's start a conversation.
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-secondary-foreground/70">
            Whether you're scoping a claims or underwriting workflow, want a working session on
            governance, or want to explore a partnership — we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Form + reasons */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
                Message us
              </span>
              <h2 className="mt-2 text-2xl font-bold">Send us a message</h2>

              <div className="mt-6 max-w-xl rounded-lg border bg-card p-6 sm:p-7">
                {submitStatus === 'success' && (
                  <div className="py-6 text-center">
                    <CheckCircle className="mx-auto mb-4 h-14 w-14 text-success" />
                    <h3 className="mb-2 text-xl font-bold">Message sent!</h3>
                    <p className="mb-6 text-muted-foreground">
                      We'll get back to you as soon as possible.
                    </p>
                    <Button variant="outline" onClick={reset}>
                      Send another message
                    </Button>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="py-6 text-center">
                    <AlertCircle className="mx-auto mb-4 h-14 w-14 text-destructive" />
                    <h3 className="mb-2 text-xl font-bold">Something went wrong</h3>
                    <p className="mb-6 text-muted-foreground">
                      {errorMessage || 'Please try again or email us at sales@criticalbridge.ai'}
                    </p>
                    <Button onClick={reset}>Try Again</Button>
                  </div>
                )}

                {submitStatus === 'idle' && (
                  <form className="grid gap-4" onSubmit={handleSubmit}>
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="hidden"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                    />

                    <div className="grid gap-4 sm:grid-cols-2">
                      <label className={labelClass}>
                        First name
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="Jane"
                          required
                          disabled={isSubmitting}
                        />
                      </label>
                      <label className={labelClass}>
                        Last name
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          className={inputClass}
                          placeholder="Doe"
                          required
                          disabled={isSubmitting}
                        />
                      </label>
                    </div>

                    <label className={labelClass}>
                      Work email
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="jane@carrier.com"
                        required
                        disabled={isSubmitting}
                      />
                    </label>

                    <label className={labelClass}>
                      Company
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Carrier Mutual"
                        disabled={isSubmitting}
                      />
                    </label>

                    <label className={labelClass}>
                      Subject
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isSubmitting}
                      >
                        <option value="demo">Book a working session</option>
                        <option value="trial">Start the Discovery Trial</option>
                        <option value="sales">Sales inquiry</option>
                        <option value="support">Technical support</option>
                        <option value="partnership">Partnership opportunity</option>
                        <option value="press">Press &amp; media</option>
                        <option value="careers">Careers</option>
                        <option value="other">Other</option>
                      </select>
                    </label>

                    <label className={labelClass}>
                      Message
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className={`resize-none ${inputClass}`}
                        placeholder="Tell us about your agent estate or the workflow you want to automate…"
                        disabled={isSubmitting}
                      ></textarea>
                    </label>

                    <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send message'
                      )}
                    </Button>

                    <p className="text-xs text-muted-foreground">
                      By submitting this form, you agree to our{' '}
                      <Link to="/privacy" className="underline hover:text-primary">
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  </form>
                )}
              </div>
            </div>

            <div>
              <span className="font-mono text-xs font-semibold uppercase tracking-widest text-primary">
                How can we help?
              </span>
              <h2 className="mt-2 text-2xl font-bold">Common reasons to reach out</h2>

              <div className="mt-6 grid gap-4">
                {CONTACT_REASONS.map((reason) => (
                  <div key={reason.title} className="rounded-lg border bg-card p-5">
                    <h3 className="text-lg font-bold">{reason.title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground">{reason.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
